import { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { Loader2, AlertCircle, Download } from 'lucide-react';

// Load worker + CJK cMaps + standard fonts locally (staged by scripts/copy-pdfjs.js).
// Using local assets avoids CDN access and lets pdf.js substitute missing/subset
// CJK glyphs (e.g. rare chars like 玥) that Chrome's native iframe viewer drops.
const PUBLIC = process.env.PUBLIC_URL || '';
pdfjsLib.GlobalWorkerOptions.workerSrc = `${PUBLIC}/pdf.worker.min.js`;
const CMAP_URL = `${PUBLIC}/cmaps/`;
const STANDARD_FONT_URL = `${PUBLIC}/standard_fonts/`;

interface Props {
  url: string;
  className?: string;
  downloadName?: string;
}

export default function PdfViewer({ url, className, downloadName }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    let pdfDoc: PDFDocumentProxy | null = null;
    let renderSeq = 0; // guards against overlapping re-renders (e.g. on resize)
    let lastWidth = 0;

    setLoading(true);
    setError(null);

    // Render all pages of the already-loaded doc at the container's current width.
    const renderPages = async () => {
      const container = containerRef.current;
      if (!pdfDoc || !container) return;
      // 容器隐藏中（移动端切到另一标签页时 display:none）直接跳过，
      // 恢复可见后 ResizeObserver 会以真实宽度重新触发，避免白渲染大尺寸画布。
      if (!container.clientWidth) return;
      const seq = ++renderSeq;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const cssWidth = container.clientWidth;
      lastWidth = cssWidth;

      // Render into a detached fragment, then swap in atomically to avoid flicker
      // and partial/duplicated canvases from overlapping renders.
      const frag = document.createDocumentFragment();
      for (let i = 1; i <= pdfDoc.numPages; i++) {
        if (cancelled || seq !== renderSeq) return;
        const page = await pdfDoc.getPage(i);
        const base = page.getViewport({ scale: 1 });
        const viewport = page.getViewport({ scale: cssWidth / base.width });

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) continue;
        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = '100%';
        canvas.style.height = 'auto';
        canvas.style.display = 'block';
        canvas.style.marginBottom = '12px';
        canvas.style.boxShadow = '0 1px 4px rgba(0,0,0,0.08)';
        frag.appendChild(canvas);

        await page.render({
          canvasContext: ctx,
          viewport,
          transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : undefined,
        }).promise;
      }
      if (cancelled || seq !== renderSeq) return;
      container.innerHTML = '';
      container.appendChild(frag);
      setLoading(false);
    };

    const load = async () => {
      try {
        const task = pdfjsLib.getDocument({
          url,
          cMapUrl: CMAP_URL,
          cMapPacked: true,
          standardFontDataUrl: STANDARD_FONT_URL,
          useSystemFonts: true,
          // 一次性整文件拉取，关闭 HTTP Range 分块请求。
          // 经代理/缓存时 Range 支持不一致会偶发 "Bad end offset" 报错，简历文件小，整拉更稳。
          disableRange: true,
          disableStream: true,
          disableAutoFetch: true,
        });
        pdfDoc = await task.promise;
        if (cancelled) return;
        await renderPages();
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.message || 'PDF 加载失败');
          setLoading(false);
        }
      }
    };

    load();

    // On resize, re-render pages from the SAME doc (no re-download). Only when the
    // width actually changed by a meaningful amount, to avoid needless churn.
    let resizeTimer: any;
    const ro = new ResizeObserver(() => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const w = containerRef.current?.clientWidth || 0;
        if (!cancelled && pdfDoc && Math.abs(w - lastWidth) > 4) renderPages();
      }, 300);
    });
    if (containerRef.current) ro.observe(containerRef.current);

    return () => {
      cancelled = true;
      clearTimeout(resizeTimer);
      ro.disconnect();
      pdfDoc?.destroy().catch(() => {});
    };
  }, [url]);

  return (
    <div className="relative w-full h-full overflow-auto bg-gray-100">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center text-gray-400 z-10 bg-gray-100/70">
          <Loader2 size={20} className="animate-spin mr-2" />
          <span className="text-sm">正在渲染简历...</span>
        </div>
      )}
      {error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500 gap-3 z-10 bg-gray-100">
          <AlertCircle size={32} className="text-gray-300" />
          <p className="text-sm">{error}</p>
          <a href={url} download={downloadName} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700">
            <Download size={14} />下载查看
          </a>
        </div>
      )}
      <div ref={containerRef} className="p-3" />
    </div>
  );
}
